import { Box, Container, Link, Stack, Typography } from "@mui/material";
import { usePageMeta } from "src/hooks/usePageMeta";
import { glassCardSx } from "src/theme/glassDialog";
import { useT } from "src/i18n";

// Contact details are identifiers, not translatable copy — they stay here as
// constants (same convention as ibetoni-site's Yhteystiedot). Only the labels
// around them live in the i18n bundles.
const CONTACT_NAME = "Juha Urho";
const CONTACT_EMAIL = "juha.urho@ibetoni.fi";
const CONTACT_PHONE = "040 533 4611";
const CONTACT_PHONE_TEL = "+358405334611";
const GENERAL_EMAIL = "info@betonijerry.fi";
const PRIVACY_EMAIL = "tietosuoja@betonijerry.fi";
const OPERATOR = "PumiNet Oy";
const OPERATOR_BUSINESS_ID = "3091336-1";

const LINK_SX = { color: "text.primary", fontSize: "1.05rem", fontWeight: 500 };

// One labelled datapoint. The value is either plain text or a link element.
function Row({ label, children }) {
    return (
        <Box>
            <Typography variant="overline" sx={{ color: "text.secondary", display: "block", lineHeight: 1.6 }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

export default function Yhteystiedot() {
    const t = useT();

    usePageMeta({
        title: t("contact.pageTitle"),
        description: t("contact.pageDescription", { operator: OPERATOR, businessId: OPERATOR_BUSINESS_ID }),
        path: "/yhteystiedot",
    });

    return (
        <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
            <Typography
                variant="h3"
                component="h1"
                sx={{ fontFamily: "'Black Ops One', serif", letterSpacing: "0.04em", mb: 1.5 }}
            >
                {t("contact.heading")}
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: "56ch" }}>
                {t("contact.lead")}
            </Typography>

            {/* Named contact — the primary route. Glass card matches /tilaa. */}
            <Box
                sx={(theme) => ({
                    mt: { xs: 4, md: 5 },
                    p: { xs: 2.5, md: 3.5 },
                    ...glassCardSx(theme),
                })}
            >
                <Stack spacing={2.5}>
                    <Row label={t("contact.personLabel")}>
                        <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
                            {CONTACT_NAME}
                        </Typography>
                    </Row>
                    <Row label={t("contact.phoneLabel")}>
                        <Link href={`tel:${CONTACT_PHONE_TEL}`} underline="hover" sx={LINK_SX}>
                            {CONTACT_PHONE}
                        </Link>
                    </Row>
                    <Row label={t("contact.emailLabel")}>
                        <Link href={`mailto:${CONTACT_EMAIL}`} underline="hover" sx={LINK_SX}>
                            {CONTACT_EMAIL}
                        </Link>
                    </Row>
                </Stack>
            </Box>

            <Stack spacing={{ xs: 3, md: 4 }} sx={{ mt: { xs: 4, md: 5 } }}>
                <Row label={t("contact.generalLabel")}>
                    <Link href={`mailto:${GENERAL_EMAIL}`} underline="hover" sx={LINK_SX}>
                        {GENERAL_EMAIL}
                    </Link>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, maxWidth: "56ch" }}>
                        {t("contact.requestHint")}
                    </Typography>
                </Row>

                {/* Data-protection mailbox — a data subject exercising a GDPR
                    right needs a route from the page called "Yhteystiedot",
                    not just from the tietosuojaseloste. */}
                <Row label={t("contact.privacyLabel")}>
                    <Link href={`mailto:${PRIVACY_EMAIL}`} underline="hover" sx={LINK_SX}>
                        {PRIVACY_EMAIL}
                    </Link>
                </Row>

                {/* Operator block — who is actually behind the service, and the
                    standing "we don't pump ourselves" clarification (see the
                    marketplace-transparency rules in CLAUDE.md). */}
                <Row label={t("contact.operatorLabel")}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {t("contact.operatorValue", { operator: OPERATOR, businessId: OPERATOR_BUSINESS_ID })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, maxWidth: "56ch" }}>
                        {t("contact.operatorNote")}
                    </Typography>
                </Row>
            </Stack>
        </Container>
    );
}
